import { h as __h } from "preact";
import { signal } from "@preact/signals";
/** @import { Signal } from "@preact/signals"; */
import { Component, h } from "preact";
import { groupResponse } from "~/backend/validate.js";
/** @import { adminMemberResponse } from "~/backend/validate.js"; */
import { ApiError, authedApiRequest, paginatedResponse, query } from "~/lib/api.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
/** @import { Placement } from "~/util/float.js"; */
import { popover } from "~/util/popover.js";
import Query from "~/util/query.js";
import Button from "~/component/Button.js";
import Icon from "~/component/Icon.js";
import Combobox from "~/admin/component/Combobox.js";
import adminStyle from "~/admin/lib/style.js";
import util from "~/style/util.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./manageGroups.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./manageGroups.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @param {{ anchor: HTMLButtonElement; orgId: string; placement: Placement }} props
 * @param {adminMemberResponse[]} members
 */
export const manageGroups = async ({
  anchor,
  orgId,
  placement
}, ...members) => {
  const user_ids = members.map(member => member.id);
  const res = await popover({
    anchor,
    placement,
    content: (/** @type {(result?: Map<string, boolean>) => void} */resolve) => h(PopoverChildren, {
      resolve,
      orgId,
      members
    })
  });
  if (!res?.size) return;
  try {
    await Promise.all(res.entries().map(([groupId, value]) => {
      if (value) {
        return authedApiRequest("POST", `/v2/orgs/${orgId}/groups/${groupId}/members`, {
          json: {
            user_ids
          }
        });
      } else {
        return authedApiRequest("DELETE", `/v2/orgs/${orgId}/groups/${groupId}/members`, {
          json: {
            user_ids
          }
        });
      }
    }));
    Query.invalidate([`/ui/admin/orgs/${orgId}/members`], ...user_ids.map(id => [`/ui/admin/orgs/${orgId}/members/${id}`]), [`/v2/orgs/${orgId}/groups`], ...res.keys().map(groupId => [`/v2/orgs/${orgId}/groups/${groupId}`]));
    return true;
  } catch (err) {
    alert(ApiError.from(err).message, {
      type: "error"
    });
    return;
  }
};

/**
 * @extends {Component<PopoverChildrenProps>}
 *
 * @typedef {object} PopoverChildrenProps
 * @property {string} orgId
 * @property {adminMemberResponse[]} members
 * @property {(result?: Map<string, boolean>) => void} resolve
 */
class PopoverChildren extends Component {
  query = signal("");
  groups = query(() => ({
    key: [`/v2/orgs/${this.props.orgId}/groups`, {
      limit: 5,
      q: this.query.value
    }],
    enabled: Boolean(this.query.value),
    keepPreviousData: Boolean(this.query.value)
  }), {
    schema: paginatedResponse(groupResponse)
  });

  /** Map of already-assigned groups to it's checkbox state */
  get currentGroups() {
    /** @type {Map<string, { id: string; name: string; count: number; state: true | "some" }>} */
    const out = new Map();
    const members = this.props.members;
    members.forEach(member => {
      member.groups.forEach(group => {
        const count = (out.get(group.id)?.count ?? 0) + 1;
        out.set(group.id, {
          id: group.id,
          name: group.name,
          count,
          state: count === members.length ? true : "some"
        });
      });
    });
    return out;
  }

  /** @param {string} groupId */
  getCheckboxState(groupId) {
    const original = this.currentGroups.get(groupId)?.state ?? false;
    const submission = this.submission.value.get(groupId);
    const state = submission ?? original;
    return {
      original,
      submission,
      state
    };
  }

  /** @type {Signal<Map<string, boolean>>} */
  submission = signal(new Map());
  render() {
    const data = this.query.value ? this.groups.data?.data ?? [] : [...this.currentGroups.values()];
    return __h("div", {
      "class": css.popover
    }, h(Combobox, {
      query: this.query,
      loading: this.groups.loading,
      options: data.map(group => {
        const {
          state
        } = this.getCheckboxState(group.id);
        return {
          id: group.id,
          label: group.name,
          render: () => __h("div", {
            "class": css.group
          }, __h("div", {
            "class": css.text + " " + util.truncate
          }, group.name), state === true && __h(Icon, {
            name: "check",
            size: 14
          }), state === "some" && __h(Icon, {
            name: "indeterminate",
            size: 14
          }))
        };
      }),
      onSelect: option => {
        const {
          state,
          original
        } = this.getCheckboxState(option.id);
        const input = state !== true;
        if (input === original) {
          const val = new Map(this.submission.value);
          val.delete(option.id);
          this.submission.value = val;
        } else {
          this.submission.value = new Map(this.submission.value).set(option.id, input);
        }
      }
    }), __h("div", {
      "class": adminStyle.actions
    }, __h(Button, {
      level: "primary",
      disabled: this.submission.value.size === 0,
      onClick: () => this.props.resolve(this.submission.value)
    }, "Update groups"), __h(Button, {
      onClick: () => this.props.resolve()
    }, "Cancel")));
  }
}